/**
 * O `fetch` que faz a chamada de Edge Function sair pela MESMA ORIGEM da página, em desenvolvimento.
 *
 * O PROBLEMA QUE ELE RESOLVE, medido em 19/09/2026 no painel da porta 8000: gravar um usuário novo
 * devolvia "Erro ao adicionar: Failed to send a request to the Edge Function". A causa não estava no
 * app nem na função: cada Edge Function guarda a própria lista de origens permitidas
 * (`ORIGENS_PERMITIDAS`, no `index.ts` de cada função), e a lista publicada em PRODUÇÃO nasceu
 * com as portas do portal antigo (5500, 8080, 8081, 4200). O navegador manda a pergunta de permissão
 * antes do POST, a função responde sem liberar `http://localhost:8000`, e o navegador recusa a
 * chamada ANTES de ela sair. A mensagem do supabase-js diz "falhou ao enviar", e por isso parece
 * defeito de rede.
 *
 * POR QUE ESTA SOLUÇÃO, e não publicar a lista corrigida: a lista corrigida já está no repositório e
 * já foi publicada em homologação, mas publicar em PRODUÇÃO é uma ação que esta sessão não executa.
 * Enquanto isso, a pergunta de permissão pode simplesmente deixar de existir: ela só acontece quando
 * a origem da página é diferente da origem do pedido. Com o servidor de desenvolvimento encaminhando
 * `/functions/v1/*` para o projeto Supabase, o pedido passa a sair para `http://localhost:8000`, que
 * é a própria página, e o navegador nem pergunta.
 *
 * O QUE ELE NÃO MUDA, e isto é o ponto: nada em produção. O build publicado não usa este `fetch`, e
 * o caminho que o Render e o site publicado exercitam continua sendo o mesmo de sempre. O desvio é
 * do servidor de desenvolvimento, e morre com ele.
 *
 * O CAMINHO CONTINUA IDÊNTICO (`/functions/v1/<nome>`), e só a origem muda. É de propósito: as
 * suítes interceptam por caminho, e trocar o caminho as quebraria sem ninguém perceber.
 */

/** O que o app informa para montar o desvio. */
export interface OpcoesMesmaOrigem {
  // A URL do projeto Supabase, a mesma que a fonte de dados recebe (`https://<ref>.supabase.co`).
  readonly urlDoProjeto: string;
  // A origem da página, que no app é `window.location.origin` (`http://localhost:8000`).
  readonly origemDaPagina: string;
  // O `fetch` de verdade. O app passa o do navegador; a suíte deste arquivo passa um de mentira.
  readonly fetchBase: typeof fetch;
}

/**
 * Devolve um `fetch` que reescreve SÓ as chamadas de Edge Function, e repassa todo o resto intacto.
 *
 * Banco, Storage e login continuam saindo para o Supabase direto, porque esses três já respondem a
 * qualquer origem e nunca foram o problema. Reescrever o que funciona seria alargar o desvio sem
 * necessidade, e cada coisa a mais no desvio é uma diferença a mais entre o que se testa aqui e o
 * que roda publicado.
 */
export function fetchDeFuncoesNaMesmaOrigem(opcoes: OpcoesMesmaOrigem): typeof fetch {
  // A barra final é retirada para o prefixo não ficar com duas barras quando a URL vier com uma.
  const projeto = opcoes.urlDoProjeto.replace(/\/+$/, '');
  // O começo exato que identifica uma chamada de função: só ele é reescrito.
  const prefixo = `${projeto}/functions/v1/`;
  // A mesma parte final, colada na origem da página. A origem também perde a barra final, pelo
  // mesmo motivo do projeto.
  const destino = `${opcoes.origemDaPagina.replace(/\/+$/, '')}/functions/v1/`;

  // Troca o endereço quando ele é de função, e devolve o mesmo texto quando não é.
  const reescrever = (endereco: string): string =>
    endereco.startsWith(prefixo) ? destino + endereco.slice(prefixo.length) : endereco;

  // O `fetch` que o cliente do Supabase vai usar no lugar do global.
  return async (entrada: RequestInfo | URL, inicio?: RequestInit): Promise<Response> => {
    // Caso 1, o único que o supabase-js usa hoje: o endereço vem como texto.
    if (typeof entrada === 'string') return opcoes.fetchBase(reescrever(entrada), inicio);
    // Caso 2: um objeto `URL`, que vira texto pela mesma regra.
    if (entrada instanceof URL) return opcoes.fetchBase(reescrever(entrada.toString()), inicio);
    // Caso 3: um `Request` pronto. Ele é remontado com o endereço novo, preservando método, corpo e
    // cabeçalhos; trocar o campo `url` de um `Request` não é permitido, e por isso ele é refeito.
    const requisicao = entrada as Request;
    const novo = reescrever(requisicao.url);
    // Endereço que não é de função: o `Request` segue como veio, sem cópia desnecessária.
    if (novo === requisicao.url) return opcoes.fetchBase(requisicao, inicio);
    return opcoes.fetchBase(new Request(novo, requisicao), inicio);
  };
}
